"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { Graduate } from "@/types/graduate";

interface GraduateDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  graduate: Graduate | null;
}

export default function GraduateDetailsModal({
  isOpen,
  onClose,
  graduate,
}: GraduateDetailsModalProps) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  // Measure the photo before showing it so the layout can adapt to its
  // aspect ratio (portrait next to the info, landscape above it) without
  // shifting once it loads.
  useEffect(() => {
    setDims(null);
    if (!graduate?.image) return;

    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) {
        setDims({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setDims({ w: 4, h: 3 });
      }
    };
    img.src = graduate.image;

    return () => {
      cancelled = true;
    };
  }, [graduate?.image]);

  if (!isOpen || !graduate) return null;

  const isLandscape = dims ? dims.w >= dims.h : true;
  const hasImage = Boolean(graduate.image);

  const photo =
    hasImage && dims ? (
      <Image
        src={graduate.image!}
        alt={graduate.name}
        width={dims.w}
        height={dims.h}
        unoptimized
        className={`w-auto h-auto max-w-full rounded-lg shadow-lg ${
          isLandscape
            ? "max-h-[35vh] sm:max-h-[45vh] lg:max-h-[55vh]"
            : "max-h-[42vh] lg:max-h-[72vh]"
        }`}
      />
    ) : hasImage ? (
      // Photo still being measured (usually instant, it is already cached)
      <div className="w-full h-40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
      </div>
    ) : (
      <div className="w-64 h-64 bg-gray-200 rounded-full flex items-center justify-center">
        <svg
          className="w-32 h-32 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      </div>
    );

  const info = (
    <div className="flex flex-col justify-center space-y-3">
      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
        {graduate.name}
      </h3>

      <div className="space-y-1">
        <p className="text-lg lg:text-xl text-accent font-medium">
          {graduate.helicopter_type}
        </p>

        <p className="text-base lg:text-lg text-gray-600">
          Graduated:{" "}
          {new Date(graduate.graduation_date).toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-base lg:text-lg font-semibold text-gray-900 mb-1">
          About
        </h4>
        <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
          {graduate.description}
        </p>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl shadow-xl w-full max-h-[95vh] flex flex-col overflow-hidden ${
          isLandscape ? "max-w-5xl" : "max-w-6xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6 flex flex-col min-h-0">
          {/* Header with close button */}
          <div className="flex justify-between items-center mb-3 shrink-0">
            <h2 className="text-2xl font-bold text-gray-900">
              Graduate Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {isLandscape ? (
            /* Landscape photo: full width on top, info below */
            <div className="flex flex-col gap-4 min-h-0">
              <div className="flex items-center justify-center shrink-0">
                {photo}
              </div>
              <div className="min-h-0 overflow-y-auto">{info}</div>
            </div>
          ) : (
            /* Portrait photo: photo next to the info, photo gets the larger column */
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6 min-h-0">
              <div className="flex items-center justify-center lg:col-span-3 min-h-0">
                {photo}
              </div>
              <div className="lg:col-span-2 flex flex-col justify-center min-h-0 overflow-y-auto">
                {info}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
